import { z } from 'zod';

import { uuidSchema, timezoneSchema, listParamsSchema } from './common';

// Enums
export const statusEnum = z.enum(['ACTIVE', 'INACTIVE']);

export const locationTypeEnum = z.enum([
  'HEADQUARTERS',
  'OFFICE',
  'REMOTE',
  'WAREHOUSE',
  'OTHER',
]);

export const flsaStatusEnum = z.enum(['EXEMPT', 'NON_EXEMPT']);

// Department
export const createDepartmentSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(200),
  parentDepartmentId: uuidSchema.optional().nullable(),
  headEmployeeId: uuidSchema.optional().nullable(),
  costCenterId: z.string().max(50).optional().nullable(),
});

export const updateDepartmentSchema = createDepartmentSchema.partial().extend({
  status: statusEnum.optional(),
});

export const departmentListParamsSchema = listParamsSchema.extend({
  status: statusEnum.optional(),
  parentDepartmentId: uuidSchema.optional().nullable(),
});

// Location
export const createLocationSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(200),
  type: locationTypeEnum.default('OFFICE'),
  addressLine1: z.string().max(200).optional().nullable(),
  addressLine2: z.string().max(200).optional().nullable(),
  city: z.string().max(100).optional().nullable(),
  state: z.string().max(100).optional().nullable(),
  postalCode: z.string().max(20).optional().nullable(),
  country: z.string().min(2).max(2), // ISO 3166-1 alpha-2
  timezone: timezoneSchema,
});

export const updateLocationSchema = createLocationSchema.partial().extend({
  status: statusEnum.optional(),
});

export const locationListParamsSchema = listParamsSchema.extend({
  status: statusEnum.optional(),
  type: locationTypeEnum.optional(),
  country: z.string().optional(),
});

// Job
export const createJobSchema = z.object({
  code: z.string().min(1).max(50),
  title: z.string().min(1).max(200),
  summary: z.string().max(500).optional().nullable(),
  description: z.string().max(10000).optional().nullable(),
  jobFamilyId: uuidSchema.optional().nullable(),
  jobLevel: z.string().max(50).optional().nullable(),
  flsaStatus: flsaStatusEnum.optional().nullable(),
});

export const updateJobSchema = createJobSchema.partial().extend({
  status: statusEnum.optional(),
});

export const jobListParamsSchema = listParamsSchema.extend({
  status: statusEnum.optional(),
  jobFamilyId: uuidSchema.optional(),
  flsaStatus: flsaStatusEnum.optional(),
});

// Type exports
export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>;
export type UpdateDepartmentInput = z.infer<typeof updateDepartmentSchema>;
export type DepartmentListParamsInput = z.infer<typeof departmentListParamsSchema>;

export type CreateLocationInput = z.infer<typeof createLocationSchema>;
export type UpdateLocationInput = z.infer<typeof updateLocationSchema>;
export type LocationListParamsInput = z.infer<typeof locationListParamsSchema>;

export type CreateJobInput = z.infer<typeof createJobSchema>;
export type UpdateJobInput = z.infer<typeof updateJobSchema>;
export type JobListParamsInput = z.infer<typeof jobListParamsSchema>;
